import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import {
    Button,
    Divider,
    IconButton,
    LoadingButton,
    Menu,
    MenuItem,
} from '@biq/apex-ui';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Breadcrumbs } from 'components/breadcrumbs/Breadcrumbs';
import {
    menuItemStyles,
    styles as headerWithActionButtonsStyles,
} from 'components/update-page-header/update-page-header-helper';
import { ConfirmationModal } from 'components/confirmation-modal/ConfirmationModal';
import { CancelModal } from 'components/cancel-modal/CancelModal';
import { headerTemplateStyles } from './header-with-action-buttons-helper';
import React, { Children, useState } from 'react';
import { globalEllipsisTextStyles } from 'lib';
import { SaveModalProps } from 'components/save-modal/save-modal-helper';
import { SaveModal } from 'components/save-modal/SaveModal';

export interface DynamicMenuItem {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    sx?: any;
    dividerAfter?: boolean;
}

export interface ConfirmationModalConfig {
    confirmationModal: boolean;
    confirmationKey: string;
    confirmationButtonText: string;
    hasConfirmationTextField: boolean;
    confirmationCancelButtonText: string;
    confirmationHeadingText: string;
    confirmationInputLabelText: string;
    confirmationSubHeadingText: React.JSX.Element;
    handleConfirmation: () => void;
    handleConfirmationModal: () => void;
    children?: React.ReactNode;
}

export interface HeaderWithActionButtonsProps {
    readOnly?: boolean;
    includeBreadcrumbs: boolean;
    breadCrumStatus?: string;
    breadCrumType?: string;
    breadCrumData?: string;
    breadCrumLastItem?: string;
    headingText: string;
    isLoading: boolean;
    isChangesUnsaved?: boolean;
    unsavedChangesConfirmationText?: string;
    openCancelModal?: boolean;
    handleCancelButton: () => void;
    handleCancelModal?: () => void;
    handleCancelModalConfirmation: () => void;
    handleSaveAndCloseButton: () => void;
    handleSaveButton: () => void;
    saveModalConfig?: SaveModalProps;
    isMenuComponentVisible: boolean;
    menuWidth?: string;
    menuItems?: DynamicMenuItem[];
    confirmationModalConfig?: ConfirmationModalConfig;
    children?: React.ReactNode;
}

export const HeaderWithActionButtons = ({
    readOnly,
    includeBreadcrumbs,
    breadCrumStatus,
    breadCrumType,
    breadCrumData,
    breadCrumLastItem,
    headingText,
    isLoading,
    isChangesUnsaved,
    unsavedChangesConfirmationText,
    openCancelModal,
    handleCancelButton,
    handleCancelModal,
    handleCancelModalConfirmation,
    handleSaveAndCloseButton,
    handleSaveButton,
    saveModalConfig,
    isMenuComponentVisible,
    menuWidth,
    menuItems,
    confirmationModalConfig,
}: HeaderWithActionButtonsProps): React.JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (item: DynamicMenuItem): void => {
        item.onClick();
        setAnchorEl(null);
    };

    return (
        <>
            <Grid sx={headerTemplateStyles.headerContainer}>
                <Grid sx={headerTemplateStyles.headerDetailsStyles}>
                    <Box sx={headerTemplateStyles.breadcrumbsContainer}>
                        {includeBreadcrumbs ? (
                            <Breadcrumbs
                                status={breadCrumStatus}
                                breadCrumType={breadCrumType}
                                breadCrumData={breadCrumData}
                                breadCrumLastItem={breadCrumLastItem}
                            />
                        ) : (
                            <></>
                        )}
                    </Box>
                    <Box width={'100%'}>
                        <Typography
                            sx={{
                                ...headerTemplateStyles.headingTextStyles,
                                ...globalEllipsisTextStyles,
                            }}
                            title={headingText}
                        >
                            {headingText}
                        </Typography>
                    </Box>
                </Grid>
                <Grid>
                    {!readOnly ? (
                        <Grid sx={headerTemplateStyles.buttonContainer}>
                            {isChangesUnsaved ? (
                                <Grid>
                                    <Typography
                                        sx={
                                            headerTemplateStyles.unsavedChangesTextStyles
                                        }
                                    >
                                        Unsaved changes
                                    </Typography>
                                </Grid>
                            ) : null}
                            <Grid>
                                <Button
                                    size='small'
                                    variant='text'
                                    onClick={handleCancelButton}
                                    sx={headerTemplateStyles.textButtonStyles}
                                >
                                    Cancel
                                </Button>
                            </Grid>
                            <Grid>
                                <Button
                                    size='small'
                                    variant='outlined'
                                    onClick={handleSaveAndCloseButton}
                                    sx={{
                                        ...headerTemplateStyles.textButtonStyles,
                                        ...headerTemplateStyles.saveAndCloseButtonStyles,
                                    }}
                                >
                                    Save & Close
                                </Button>
                            </Grid>
                            <Grid>
                                <LoadingButton
                                    size='small'
                                    onClick={handleSaveButton}
                                    disabled={isLoading}
                                    color='primary'
                                    loading={isLoading}
                                    variant='contained'
                                    position='end'
                                    loadingVariant='withoutIcon'
                                    loaderIcon={
                                        <CircularProgress
                                            variant='indeterminate'
                                            disableShrink
                                        />
                                    }
                                >
                                    Save
                                </LoadingButton>
                            </Grid>
                            {isMenuComponentVisible && (
                                <Grid>
                                    <IconButton
                                        aria-label='more'
                                        id='delete-button'
                                        aria-controls={
                                            open ? 'delete-menu' : undefined
                                        }
                                        aria-expanded={
                                            open ? 'true' : undefined
                                        }
                                        aria-haspopup='true'
                                        onClick={handleClick}
                                        sx={
                                            headerWithActionButtonsStyles.iconButtonStyles
                                        }
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Menu
                                        id='delete-menu'
                                        aria-labelledby='delete-button'
                                        anchorEl={anchorEl}
                                        open={open}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        sx={menuItemStyles(
                                            menuWidth ?? '184px'
                                        )}
                                    >
                                        {Children.toArray(
                                            menuItems?.map((item) => (
                                                <Grid>
                                                    <MenuItem
                                                        onClick={(): void =>
                                                            handleMenuItemClick(
                                                                item
                                                            )
                                                        }
                                                        sx={item.sx}
                                                    >
                                                        <Box
                                                            sx={
                                                                headerTemplateStyles.menuItemContainer
                                                            }
                                                        >
                                                            {item.icon}
                                                            <Typography
                                                                sx={
                                                                    headerTemplateStyles.menuItemTextStyles
                                                                }
                                                            >
                                                                {item.label}
                                                            </Typography>
                                                        </Box>
                                                    </MenuItem>
                                                    {item.dividerAfter && (
                                                        <Divider
                                                            sx={
                                                                headerTemplateStyles.dividerStyles
                                                            }
                                                        />
                                                    )}
                                                </Grid>
                                            ))
                                        )}
                                    </Menu>
                                </Grid>
                            )}
                        </Grid>
                    ) : null}
                </Grid>
            </Grid>
            {confirmationModalConfig?.confirmationModal && (
                <ConfirmationModal {...confirmationModalConfig} />
            )}
            {openCancelModal && (
                <CancelModal
                    cancelModal={openCancelModal}
                    handleCancelModal={handleCancelModal}
                    cancelButtonText='Close without saving'
                    cancelHeadingText='Are you sure you want to abandon your changes?'
                    cancelSubHeadingText={unsavedChangesConfirmationText}
                    handleCancel={handleCancelModalConfirmation}
                />
            )}
            {saveModalConfig && (
                <SaveModal {...saveModalConfig}>
                    {saveModalConfig.children}
                </SaveModal>
            )}
        </>
    );
};
